import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Mail,
  Truck,
  Copy,
  Share2,
} from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/format";
import { track, trackAdsConversion } from "../lib/analytics";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const [related, setRelated] = useState([]);
  const { clear } = useCart();
  const cleared = useRef(false);
  const purchaseTracked = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    // Poll for up to ~90s with progressive backoff. Stripe LIVE 3DS payments
    // sometimes take 30-60s for the webhook to fire — much longer than the
    // previous 16s window which caused legitimate purchases to be flagged
    // as "expired" while the customer was actually charged.
    const schedule = [1500, 2000, 2500, 3000, 4000, 5000, 6000, 8000, 10000, 12000, 12000, 12000];
    const maxAttempts = schedule.length;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      attempts += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("success");
          try {
            const orderRes = await api.get(`/orders/${r.data.order_id}`);
            setOrder(orderRes.data);
          } catch {
            setOrder({ id: r.data.order_id, total: r.data.amount_total / 100 });
          }
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (r.data.status === "expired") {
          setStatus("failed");
          return;
        }
        const wait = schedule[Math.min(attempts - 1, schedule.length - 1)];
        setTimeout(poll, wait);
      } catch {
        const wait = schedule[Math.min(attempts - 1, schedule.length - 1)];
        setTimeout(poll, wait);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  // Fetch related products (different from purchased) for cross-sell
  useEffect(() => {
    if (status !== "success") return;
    api.get("/products").then((r) => {
      const purchasedIds = new Set(
        (order?.items || []).map((i) => i.product_id)
      );
      const others = r.data
        .filter((p) => !purchasedIds.has(p.id))
        .sort(() => Math.random() - 0.5)
        .slice(0, 4);
      setRelated(others);
    });
  }, [status, order]);

  // Track GA4 + Google Ads purchase conversion once
  useEffect(() => {
    if (status !== "success" || !order || purchaseTracked.current) return;
    purchaseTracked.current = true;
    const items = (order.items || []).map((i) => ({
      item_id: i.product_id,
      item_name: i.name,
      price: i.price,
      quantity: i.quantity,
    }));
    track("purchase", {
      transaction_id: order.id,
      value: order.total,
      currency: "EUR",
      coupon: order.coupon_code || undefined,
      items,
    });
    // Google Ads conversion (Performance Max campaign)
    trackAdsConversion({
      transaction_id: order.id,
      value: order.total,
      currency: "EUR",
    });
  }, [status, order]);

  const copyOrderId = () => {
    if (!order?.id) return;
    navigator.clipboard.writeText(order.id);
    toast.success("Numéro de commande copié");
  };

  const shareNative = async () => {
    if (typeof navigator.share !== "function") {
      navigator.clipboard.writeText("https://nordstellatech.fr");
      toast.success("Lien copié — partagez-le !");
      return;
    }
    try {
      await navigator.share({
        title: "Nord Stella Tech",
        text: "Je viens de commander chez Nord Stella Tech, vous allez adorer !",
        url: "https://nordstellatech.fr",
      });
      track("share", { method: "web_share" });
    } catch {
      /* user cancelled */
    }
  };

  if (status === "polling") {
    return (
      <div
        className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center"
        data-testid="checkout-success-page"
      >
        <div data-testid="status-polling">
          <Loader2 size={36} strokeWidth={1.25} className="mx-auto animate-spin text-[#C5CDD8]" />
          <h1 className="font-display mt-6 text-3xl font-bold uppercase tracking-tighter text-white">
            Vérification du paiement
          </h1>
          <p className="mt-3 text-sm text-white/60">Merci de patienter quelques instants...</p>
        </div>
      </div>
    );
  }

  if (status === "failed" || status === "timeout") {
    return (
      <div
        className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center"
        data-testid="checkout-success-page"
      >
        <div data-testid="status-failed">
          <XCircle size={36} strokeWidth={1.25} className="mx-auto text-[#FF6B6B]" />
          <h1 className="font-display mt-6 text-3xl font-bold uppercase tracking-tighter text-white">
            {status === "timeout" ? "Vérification trop longue" : "Paiement échoué"}
          </h1>
          {status === "timeout" ? (
            <>
              <p className="mt-4 text-sm text-white/70 leading-relaxed">
                Si vous avez été prélevé, votre commande est <strong className="text-white">probablement en cours de validation</strong>.
                Stripe peut mettre jusqu'à 2 minutes à confirmer le paiement (notamment avec l'authentification 3DSecure).
              </p>
              <p className="mt-3 text-sm text-white/60">
                Cliquez sur "Vérifier à nouveau" ou consultez vos commandes dans <Link to="/account" className="underline hover:text-white">votre espace client</Link>.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-8 inline-block border border-[#FFD700] bg-[#FFD700] text-[#001F3F] hover:bg-[#001F3F] hover:text-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
                data-testid="status-retry-btn"
              >
                ↻ Vérifier à nouveau
              </button>
            </>
          ) : (
            <p className="mt-3 text-sm text-white/60">
              Votre carte a été refusée ou la session a expiré. Aucune somme n'a été prélevée. Veuillez réessayer.
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/cart"
              className="inline-block border border-white/30 text-white hover:bg-white/10 transition uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
              data-testid="status-back-cart"
            >
              Retour au panier
            </Link>
            <Link
              to="/contact"
              className="inline-block border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
            >
              Contacter le support
            </Link>
          </div>
          <p className="mt-8 text-[10px] text-white/40 uppercase tracking-widest">
            Question ? contact@nordstellatech.fr
          </p>
        </div>
      </div>
    );
  }

  // SUCCESS
  return (
    <div
      className="max-w-5xl mx-auto px-6 md:px-12 py-16"
      data-testid="checkout-success-page"
    >
      <div data-testid="status-success">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-[#A6E0B5]/40 bg-[#A6E0B5]/10 mb-6">
            <CheckCircle2
              size={32}
              strokeWidth={1.25}
              className="text-[#A6E0B5]"
            />
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            Confirmation
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold uppercase tracking-tighter text-white leading-[0.95]">
            Merci pour votre commande
          </h1>
          {order?.id && (
            <div className="mt-6 inline-flex items-center gap-3 text-xs text-white/60 uppercase tracking-widest">
              <span data-testid="order-ref">Réf : {order.id.slice(0, 8).toUpperCase()}</span>
              <button
                onClick={copyOrderId}
                className="text-white/40 hover:text-white"
                aria-label="Copier la référence"
                data-testid="copy-order-id"
              >
                <Copy size={12} />
              </button>
            </div>
          )}
        </div>

        {/* Order recap */}
        {order?.items && order.items.length > 0 && (
          <div className="mt-12 border border-white/10 bg-[#0A2A4F] p-6 md:p-8" data-testid="order-recap">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8] mb-6">
              Récapitulatif
            </div>
            <div className="space-y-3">
              {order.items.map((it) => (
                <div key={it.product_id} className="flex gap-4 text-sm">
                  <div className="w-14 h-14 bg-[#001F3F] flex-shrink-0 border border-white/5">
                    <img
                      src={it.image}
                      alt={it.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{it.name}</div>
                    <div className="text-xs text-white/60">Qté : {it.quantity}</div>
                  </div>
                  <div className="font-bold text-white whitespace-nowrap">
                    {formatPrice(it.price * it.quantity)}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 mt-6 pt-4 flex justify-between text-sm">
              <span className="font-bold uppercase tracking-widest text-white">Total payé</span>
              <span className="font-display text-2xl font-bold text-white">
                {formatPrice(order.total)}
              </span>
            </div>
          </div>
        )}

        {/* Next steps */}
        <div className="mt-12 grid sm:grid-cols-2 gap-4">
          <div className="border border-white/10 p-5 flex gap-4">
            <Mail size={20} strokeWidth={1.25} className="text-[#C5CDD8] flex-shrink-0 mt-1" />
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-white">
                Email de confirmation
              </div>
              <div className="mt-1 text-xs text-white/60">
                Envoyé à votre adresse dans quelques minutes.
              </div>
            </div>
          </div>
          <div className="border border-white/10 p-5 flex gap-4">
            <Truck size={20} strokeWidth={1.25} className="text-[#C5CDD8] flex-shrink-0 mt-1" />
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-white">
                Expédition 5–10 jours
              </div>
              <div className="mt-1 text-xs text-white/60">
                Suivi par email dès l'envoi.
              </div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link
            to="/account"
            className="border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
            data-testid="view-orders"
          >
            Mes commandes
          </Link>
          <Link
            to="/shop"
            className="border border-white/40 bg-transparent text-white hover:bg-white hover:text-[#001F3F] transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
          >
            Continuer mes achats
          </Link>
          <button
            onClick={shareNative}
            className="inline-flex items-center gap-2 border border-white/40 bg-transparent text-white hover:bg-white hover:text-[#001F3F] transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
            data-testid="share-button"
          >
            <Share2 size={14} /> Partager
          </button>
        </div>

        {/* Cross-sell */}
        {related.length > 0 && (
          <section className="mt-24 border-t border-white/10 pt-12" data-testid="cross-sell">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              Ils pourraient compléter votre commande
            </div>
            <h2 className="font-display mt-3 text-3xl font-bold uppercase tracking-tighter text-white">
              Découvrez aussi
            </h2>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
